/**
 * One tag in the KPI picker: expand/collapse its KPIs, or check the whole tag.
 */

import React, { useEffect, useRef } from 'react';
import type { CatalogKpi } from './types';

interface KpiTagGroupProps {
  label: string;
  items: CatalogKpi[];
  checkedIds: Set<string>;
  expanded: boolean;
  saving: boolean;
  onToggleExpand: () => void;
  onToggleTag: (selectAll: boolean) => void;
  onToggleOne: (kpiId: string) => void;
  expandLabel: string;
  collapseLabel: string;
  selectLabel: string;
}

export const KpiTagGroup: React.FC<KpiTagGroupProps> = ({
  label,
  items,
  checkedIds,
  expanded,
  saving,
  onToggleExpand,
  onToggleTag,
  onToggleOne,
  expandLabel,
  collapseLabel,
  selectLabel
}) => {
  const selected = items.filter((item) => checkedIds.has(item.kpiId)).length;
  const allOn = items.length > 0 && selected === items.length;
  const someOn = selected > 0 && !allOn;
  const box = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (box.current) box.current.indeterminate = someOn;
  }, [someOn]);

  return (
    <li>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="flex items-center gap-1 min-w-0 flex-1 text-left text-[11px] font-medium text-[var(--color-surface-text)]"
          aria-expanded={expanded}
          aria-label={expanded ? collapseLabel : expandLabel}
          onClick={onToggleExpand}
        >
          <span className="w-3 text-[10px] text-[var(--color-secondary-text)]" aria-hidden>
            {expanded ? '▼' : '▶'}
          </span>
          <span className="truncate">{label}</span>
          <span className="text-[10px] text-[var(--color-secondary-text)] shrink-0">{items.length}</span>
        </button>
        <input
          ref={box}
          type="checkbox"
          className="mt-0 shrink-0"
          disabled={saving || items.length === 0}
          checked={allOn}
          aria-label={selectLabel}
          title={selectLabel}
          onChange={() => onToggleTag(!allOn)}
        />
      </div>
      {expanded && (
        <ul className="space-y-0.5 mt-0.5 pl-4">
          {items.map((item) => (
            <li key={item.kpiId}>
              <label className="flex items-start gap-1.5 text-[11px] text-[var(--color-surface-text)] cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={saving}
                  checked={checkedIds.has(item.kpiId)}
                  onChange={() => onToggleOne(item.kpiId)}
                />
                <span className="leading-tight">{item.kpiName}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};
